/**
 * Step Helper Functions
 * Pure functions that operate on step data from constants.js
 * Separated from constants to maintain clean data/logic separation
 * 
 * This module provides helper functions for working with the new STEP_DEFINITIONS structure:
 * - Direct access to step data using currentStep values from backend
 * - Content processing with placeholder replacement
 * - Template type and action retrieval
 * - No more APPLICATION_TYPES or subType concepts
 */

import { CURRENT_STEP_VALUES, STEP_DEFINITIONS } from './constants.js';
import { getContactInfo } from '../constants/externalConfig.js';

/**
 * Get step definition for a specific currentStep
 * @param {string} currentStep - Current step from backend (e.g., 'HONORS_OFFER')
 * @returns {Object|null} Complete step definition or null if not found
 */
export const getStepDefinition = (currentStep) => {
  return STEP_DEFINITIONS[currentStep] || null;
};

/**
 * Get step content for a specific currentStep
 * @param {string} currentStep - Current step from backend (e.g., 'HONORS_OFFER')
 * @returns {Object|null} Step content or null if not found
 */
export const getStepContent = (currentStep) => {
  const definition = getStepDefinition(currentStep);
  return definition?.content || null;
};

/**
 * Get step actions for a specific currentStep
 * @param {string} currentStep - Current step from backend (e.g., 'HONORS_OFFER')
 * @returns {Array} Step actions array (empty array if not found)
 */
export const getStepActions = (currentStep) => {
  const definition = getStepDefinition(currentStep);
  return definition?.actions || [];
};

/**
 * Get template type for a specific currentStep
 * @param {string} currentStep - Current step from backend (e.g., 'HONORS_OFFER')
 * @returns {string} Template type ('default' if not found)
 */
export const getTemplateType = (currentStep) => {
  const definition = getStepDefinition(currentStep);
  return definition?.templateType || 'default';
};

/**
 * Get decline modal configuration for a specific currentStep
 * @param {string} currentStep - Current step from backend (e.g., 'HONORS_OFFER')
 * @returns {Object|null} Decline modal configuration or null if not found
 */
export const getDeclineModalConfig = (currentStep) => {
  const definition = getStepDefinition(currentStep);
  return definition?.declineModal || null;
};

/**
 * Replace placeholders in content strings
 * @param {string} content - Content string with placeholders like {deadline}
 * @param {Object} replacements - Object with replacement values
 * @returns {string} Content with placeholders replaced
 */
export const replaceContentPlaceholders = (content, replacements = {}) => {
  if (!content || typeof content !== 'string') {
    return content;
  }

  let result = content;
  Object.keys(replacements).forEach(key => {
    const placeholder = `{${key}}`;
    result = result.replace(new RegExp(placeholder, 'g'), replacements[key]);
  });

  return result;
};

/**
 * Process step content by replacing placeholders with actual values
 * @param {string} currentStep - Current step from backend
 * @param {Object} applicationData - Application data for replacements
 * @returns {Object|null} Processed content with placeholders replaced
 */
export const processStepContent = (currentStep, applicationData = {}) => {
  const content = getStepContent(currentStep);
  if (!content) return null;

  const { selfService } = applicationData;
  const contactInfo = getContactInfo();
  
  // Common replacements including contact information
  const replacements = {
    // Application data placeholders
    deadline: selfService?.deadline || 'the deadline',
    
    // Contact information placeholders
    honorsScholarsEmail: contactInfo.honorsScholarsEmail,
    honorsScholarsPhone: contactInfo.honorsScholarsPhone,
    honorsScholarsContact: contactInfo.honorsScholarsContact,
    admissionsEmail: contactInfo.admissionsEmail,
    admissionsPhone: contactInfo.admissionsPhone,
    admissionsContact: contactInfo.admissionsContact,
    
    // Contact pattern placeholders
    questionsContact: contactInfo.patterns.questionsContact,
    questionsContactAdmissions: contactInfo.patterns.questionsContactAdmissions,
    officeContact: contactInfo.patterns.officeContact,
    generalQuestions: contactInfo.patterns.generalQuestions,
    statusQuestions: contactInfo.patterns.statusQuestions
  };

  // Process all string fields in the content
  const processedContent = {};
  Object.keys(content).forEach(key => {
    const value = content[key];
    if (typeof value === 'string') {
      processedContent[key] = replaceContentPlaceholders(value, replacements);
    } else {
      processedContent[key] = value;
    }
  });
  
  return processedContent;
};

/**
 * Get additional resources configuration based on currentStep
 * @param {string} currentStep - Current step from backend (e.g., 'HONORS_OFFER')
 * @returns {Object} Additional resources configuration
 */
export const getAdditionalResourcesConfig = (currentStep) => {
  const definition = getStepDefinition(currentStep);
  return definition?.additionalResources || {
    programInfo: {
      title: 'Program Information',
      description: 'Learn more about the program requirements and benefits.',
      buttonText: 'Learn More'
    },
    academicSupport: {
      title: 'Academic Support',
      description: 'Explore academic resources and support services available to you.',
      buttonText: 'View Resources'
    }
  };
};

/**
 * Check if a step should be active based on current step
 * @param {string} stepId - Step ID to check
 * @param {string} currentStep - Current step from backend
 * @returns {boolean} Whether step should be active
 */
export const isStepActive = (stepId, currentStep) => {
  // Map currentStep values to step IDs that should be active
  const activeStepMap = {
    'HONORS_INPROGRESS': 'honors-review',
    'HONORS_OFFER': 'honors-decision',
    'HONORS_DENY': 'honors-decision',
    'SCHOLARS_PREFERENCES': 'scholars-invitation',
    'SCHOLARS_PREFERENCES_SUBMITTED': 'scholars-review'
  };

  return activeStepMap[currentStep] === stepId;
};

/**
 * Get step status based on current step and step position
 * @param {string} stepId - Step ID to check
 * @param {string} currentStep - Current step from backend
 * @returns {string} Step status (COMPLETE, IN_PROGRESS, PENDING)
 */
export const getStepStatus = (stepId, currentStep) => {
  if (isStepActive(stepId, currentStep)) {
    return 'IN_PROGRESS';
  }

  // Define step order for each flow
  const stepOrder = {
    'honors-review': 1,
    'honors-decision': 2,
    'scholars-invitation': 3,
    'scholars-review': 4
  };

  const currentStepOrder = {
    'HONORS_INPROGRESS': 1,
    'HONORS_OFFER': 2,
    'HONORS_DENY': 2,
    'SCHOLARS_PREFERENCES': 2,
    'SCHOLARS_PREFERENCES_SUBMITTED': 4
  };

  const stepOrderValue = stepOrder[stepId] || 0;
  const currentOrderValue = currentStepOrder[currentStep] || 0;

  return stepOrderValue < currentOrderValue ? 'COMPLETE' : 'PENDING';
};